import React, {FC, Fragment} from "react";
import {Dialog, Transition} from "@headlessui/react";
import moment from "moment/moment";
import BookingHotelRoomRate from "../types/BookingHotelRoomRate";
import ButtonClose from "../../ui/shared/ButtonClose/ButtonClose";
import {formatNumber} from "../../common/utils/number";
import useTranslate from "../../core/languages/hooks/useTranslate";
import Loader from "../../ui/shared/Loader/Loader";

export interface BookingPriceDetailsByNightsProps {
  bookPriceDetail?: BookingHotelRoomRate[];
  totalPrice: number;
  currencySymbol: string;
  isLoadingBookPriceDetail: boolean;
  openModal: boolean;
  onClose: () => void;
}

const BookingPriceDetailsByNights: FC<BookingPriceDetailsByNightsProps> = ({
                                                                             bookPriceDetail,
                                                                             totalPrice,
                                                                             currencySymbol,
                                                                             isLoadingBookPriceDetail,
                                                                             openModal,
                                                                             onClose,
                                                                           }) => {
  const t = useTranslate();

  return (
    <Transition appear show={openModal} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-40"/>
          </Transition.Child>
          <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-4xl py-8 align-middle">
              <div
                className="flex max-h-[80vh] flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:border dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100">
                {/* Header */}
                <div
                  className="relative flex-shrink-0 border-b border-neutral-200 px-6 py-4 text-center dark:border-neutral-800">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-neutral-100">
                    {t("checkout.book_price_detail")}
                  </h3>
                  <span className="absolute left-3 top-3">
                    <ButtonClose onClick={onClose}/>
                  </span>
                </div>
                <div
                  className="flex-1 overflow-y-auto px-8 text-neutral-700 divide-y divide-neutral-200 dark:text-neutral-300">
                  {bookPriceDetail?.map((bookDetail, index) => (
                    <div
                      key={index}
                      className="flex justify-between space-x-5 py-2.5 text-neutral-6000 sm:py-4 lg:space-x-8 lg:py-5 dark:text-neutral-300"
                    >
                      <span>{t("checkout.night_of")}{" "}{moment(bookDetail.date).format("DD/MM/YYYY")}</span>
                      <span>{formatNumber(bookDetail.priceWithTaxes)}{" "}<span
                        dangerouslySetInnerHTML={{__html: currencySymbol,}}/></span>
                    </div>
                  ))}

                  {isLoadingBookPriceDetail && (
                    <Loader centerHorizontally/>
                  )}

                  <div className="flex flex-col space-y-4 py-4">
                    <div className="flex justify-between font-semibold">
                      <span>{t("checkout.total")}</span>
                      <div>
                        {formatNumber(totalPrice)}{" "}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: currencySymbol,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BookingPriceDetailsByNights;